import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import './GlobalSearch.scss';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Collapse, IconButton, Paper, TextField } from "@mui/material";
import { useDataFetcher } from "Utilities/CommonHooks";
import { ENDPOINTS } from "Models/Templates/Routes/Routes";
import { AsyncStatus } from "Models";
import { buildRoute } from "Utilities";
import { useHistory } from "react-router-dom";
import { AddEditSubRoute, CaseManagementSubRoute, ROUTES, SubRoutes } from "Navigation";

type GlobalSearchProps = {
  onSearch: (query: string) => void;
};

type ReferralSearchModel = {
  id: string;
  stradixNumber: string;
  claimId: string;
  claimNumber: string;
  clientId: string;
  clientName: string;
  claimantId: string;
  claimantFirstName: string;
  claimantLastName: string;
  claimantName: string;
  referralId: string;
  referralNumber: string;
  caseStatus: any;
  appointmentStatus: any;
}

type ReferralSearchResults = {
  data: ReferralSearchModel[];
  totalCount: number;
}

export const GlobalSearch = (props: GlobalSearchProps) => {
  const [open, setOpen] = useState(false);
  const [dontBlur, setDontBlur] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [results, resultsStatus] = useDataFetcher<ReferralSearchResults>(
    `${ENDPOINTS.referrals.base}/Search?term=${encodeURIComponent(query)}`,
    { data: [] as ReferralSearchModel[], totalCount: 0 },
    (d) => d.data,
    undefined,
    query !== undefined && query.length >= 5,
    '3.0'
  );
  const history = useHistory();

  useEffect(() => {
    const handler = setTimeout(() => {
      onChangeValue({
        target: { value: inputValue },
      } as ChangeEvent<HTMLInputElement>);
    }, 300);

    return () => clearTimeout(handler);
  }, [inputValue]);

  function onChangeValue(event: ChangeEvent<HTMLInputElement>) {
    const query = event.target.value;
    setQuery(query);
  }

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    function onGlobalKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setInputValue('');
        setQuery('');
        setOpen(false);
      }
      if (e.key === "Tab") {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onGlobalKey);
    return () => window.removeEventListener("keydown", onGlobalKey);
  }, []);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);


  function selectResult(r: ReferralSearchModel) {
    history.push(
      buildRoute(AddEditSubRoute.Referral, r.id, ROUTES.VIEW)
    );
  }

  function onIconClick() {
    if (open && inputValue) {
      history.push(
        buildRoute(ROUTES.REFERRALS + CaseManagementSubRoute.AllClaimants) + `?search=${encodeURIComponent(inputValue)}`
      );
    }
    setOpen(!open);
    setQuery('');
    setInputValue('');
    setHighlightedIndex(-1);
  }

  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // instead of the query get the actual input value
    const query = inputRef.current ? inputRef.current.value : '';
  }

  return (
    <div className="global-search-root" ref={containerRef}>
      <div className="global-search-paper">
        <Collapse in={open} orientation="horizontal" timeout={300}>
          <TextField
            inputRef={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="global-search-input"
            variant="standard"
            placeholder={'Search by Stradix number, claim ID, date, or claimant name'}
            onKeyDown={onInputKeyDown}
            onBlur={() => {
              if (dontBlur) {
                return;
              }
              setOpen(false);
              setQuery('');
              setInputValue('');
              setTimeout(() => {
                if (!containerRef.current) return;
                if (!containerRef.current.contains(document.activeElement)) {
                  setOpen(false);
                  setHighlightedIndex(-1);
                }
              }, 0);
            }}
            InputProps={{ disableUnderline: true }}
          />
        </Collapse>
        <IconButton
          aria-label={open ? "Close input" : "Open input"}
          onClick={onIconClick}
          onMouseDown={(e) => e.preventDefault()}
          className="global-search-iconbutton"
        >
          <SearchIcon />
        </IconButton>
      </div>
      {query && open && resultsStatus == AsyncStatus.Success && (
        results.data.length > 0 ? (
          <div className="global-search-results">
            {results.data.map((result, i) => (
              <div
                key={result.id}
                className={`global-search-result-item ${i === highlightedIndex ? 'highlighted' : ''}`}
                id={`result-${i}`}
                onMouseDown={(ev) => ev.preventDefault()}
                onMouseEnter={() => setHighlightedIndex(i)}
                onMouseLeave={() => setHighlightedIndex(-1)}
                onClick={() => selectResult(result)}
              >
                {result.claimantName} | {result.stradixNumber} | {result.claimNumber} | {result.clientName}
              </div>
            ))}
            {/* only show when result count is > 5 */}
            {results.totalCount > 5 && (
              <div className="global-search-result-item" onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => {
                  history.push(
                    buildRoute(ROUTES.REFERRALS + CaseManagementSubRoute.AllClaimants)
                    + `?searchKey=${encodeURIComponent(query)}`
                  );
                  setOpen(false);
                  setQuery('');
                  setInputValue('');
                  setHighlightedIndex(-1);
                }}>
                View all {results.totalCount} results
                <span style={{ marginLeft: '8px' }}>&rarr;</span>
              </div>
            )}
          </div>
        ) : (
          <div className="global-search-results no-results">
            <div className="no-results-message">
              No records found for '{query}'
            </div>
            <div
              className="view-all-cases"
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={() => {
                history.push(
                  buildRoute(ROUTES.REFERRALS + CaseManagementSubRoute.AllClaimants) +
                  `?searchKey=${encodeURIComponent(query)}`
                );
                setOpen(false);
                setQuery('');
                setInputValue('');
                setHighlightedIndex(-1);
              }}
            >
              Can't find what you're looking for? <strong>View all cases</strong> <ArrowForwardIcon className="arrow" />
            </div>
          </div>
        ))}
    </div>
  );
}